import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import React from "react"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Unified status color function - used across all components
export const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case "completed":
      return "bg-green-500 text-white border-green-500 shadow-sm hover:bg-green-600 hover:border-green-600 hover:shadow-md transition-all duration-200 cursor-pointer";
    case "picked up":
      return "bg-green-500 text-white border-green-500 shadow-sm hover:bg-green-600 hover:border-green-600 hover:shadow-md transition-all duration-200 cursor-pointer";
    case "ready for pickup":
      return "bg-blue-500 text-white border-blue-500 shadow-sm hover:bg-blue-600 hover:border-blue-600 hover:shadow-md transition-all duration-200 cursor-pointer";
    case "arrived pending pickup":
      return "bg-blue-500 text-white border-blue-500 shadow-sm hover:bg-blue-600 hover:border-blue-600 hover:shadow-md transition-all duration-200 cursor-pointer";
    case "in transit":
      return "bg-purple-500 text-white border-purple-500 shadow-sm hover:bg-purple-600 hover:border-purple-600 hover:shadow-md transition-all duration-200 cursor-pointer";
    case "shipped":
      return "bg-indigo-500 text-white border-indigo-500 shadow-sm hover:bg-indigo-600 hover:border-indigo-600 hover:shadow-md transition-all duration-200 cursor-pointer";
    case "paid":
      return "bg-emerald-500 text-white border-emerald-500 shadow-sm hover:bg-emerald-600 hover:border-emerald-600 hover:shadow-md transition-all duration-200 cursor-pointer";
    case "consolidation":
      return "bg-orange-500 text-white border-orange-500 shadow-sm hover:bg-orange-600 hover:border-orange-600 hover:shadow-md transition-all duration-200 cursor-pointer";
    case "tracking":
      return "bg-cyan-500 text-white border-cyan-500 shadow-sm hover:bg-cyan-600 hover:border-cyan-600 hover:shadow-md transition-all duration-200 cursor-pointer";
    case "pending":
      return "bg-yellow-500 text-white border-yellow-500 shadow-sm hover:bg-yellow-600 hover:border-yellow-600 hover:shadow-md transition-all duration-200 cursor-pointer";
    case "processing":
      return "bg-blue-500 text-white border-blue-500 shadow-sm hover:bg-blue-600 hover:border-blue-600 hover:shadow-md transition-all duration-200 cursor-pointer";
    case "delivered":
      return "bg-green-500 text-white border-green-500 shadow-sm hover:bg-green-600 hover:border-green-600 hover:shadow-md transition-all duration-200 cursor-pointer";
    case "cancelled":
      return "bg-red-500 text-white border-red-500 shadow-sm hover:bg-red-600 hover:border-red-600 hover:shadow-md transition-all duration-200 cursor-pointer";
    case "approved":
      return "bg-green-500 text-white border-green-500 shadow-sm hover:bg-green-600 hover:border-green-600 hover:shadow-md transition-all duration-200 cursor-pointer";
    case "rejected":
      return "bg-red-500 text-white border-red-500 shadow-sm hover:bg-red-600 hover:border-red-600 hover:shadow-md transition-all duration-200 cursor-pointer";
    case "resubmission":
      return "bg-orange-500 text-white border-orange-500 shadow-sm hover:bg-orange-600 hover:border-orange-600 hover:shadow-md transition-all duration-200 cursor-pointer";
    case "expired":
      return "bg-gray-500 text-white border-gray-500 shadow-sm hover:bg-gray-600 hover:border-gray-600 hover:shadow-md transition-all duration-200 cursor-pointer";
    case "verifying":
      return "bg-blue-500 text-white border-blue-500 shadow-sm hover:bg-blue-600 hover:border-blue-600 hover:shadow-md transition-all duration-200 cursor-pointer";
    case "active":
      return "bg-green-500 text-white border-green-500 shadow-sm hover:bg-green-600 hover:border-green-600 hover:shadow-md transition-all duration-200 cursor-pointer";
    case "inactive":
      return "bg-gray-500 text-white border-gray-500 shadow-sm hover:bg-gray-600 hover:border-gray-600 hover:shadow-md transition-all duration-200 cursor-pointer";
    default:
      return "bg-gray-500 text-white border-gray-500 shadow-sm hover:bg-gray-600 hover:border-gray-600 hover:shadow-md transition-all duration-200 cursor-pointer";
  }
};

// Date formatting function
export const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return original string if invalid
    }

    // Get day with ordinal suffix
    const day = date.getDate();
    const getOrdinalSuffix = (day: number): string => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };

    // Get month name
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const month = months[date.getMonth()];
    
    // Get year
    const year = date.getFullYear();
    
    // Get time in 12-hour format with AM/PM
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    
    return `${day}${getOrdinalSuffix(day)} ${month} ${year} ${displayHours}:${displayMinutes} ${ampm}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString; // Return original string if error
  }
};

// Utility function to decode JWT token without verification
import { decodeJWT as decodeJwtToken, isTokenExpired as isJwtExpired, isTokenInvalid, isInvalidTokenMessage } from './auth/token'

export { decodeJwtToken as decodeJWT, isJwtExpired as isTokenExpired, isTokenInvalid, isInvalidTokenMessage }

// Get token expiration time
export function getTokenExpiration(token: string): Date | null {
  try {
    const decoded = decodeJwtToken(token);
    if (!decoded || !decoded.exp) {
      return null;
    }
    
    return new Date(decoded.exp * 1000);
  } catch (error) {
    return null;
  }
}

/**
 * Utility function to extract error message from various error types
 * This helps in production where Next.js hides error details
 */
export function extractErrorMessage(error: any): string {
  if (!error) return 'An unknown error occurred';
  
  // Handle APIError instances
  if (error.name === 'APIError' && error.response?.data) {
    const responseData = error.response.data;
    
    if (responseData.detail) {
      if (Array.isArray(responseData.detail)) {
        return responseData.detail.map((err: any) => err.msg || err.message || 'Validation error').join(', ');
      }
      return responseData.detail;
    }
    
    if (responseData.message) return responseData.message;
    if (responseData.error) return responseData.error;
  }
  
  // Handle standard Error objects
  if (error.message) {
    return error.message;
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }
  
  // Handle objects with error properties
  if (error.error) return error.error;
  if (error.err) return error.err;
  
  // Fallback
  return 'An unexpected error occurred';
}

/**
 * Utility function to log detailed error information for debugging
 */
export function logErrorDetails(error: any, context: string = 'Unknown'): void {
  console.error(`[${context}] Error Details:`, {
    name: error?.name,
    message: error?.message,
    stack: error?.stack,
    status: error?.status,
    response: error?.response,
    url: error?.url,
    method: error?.method,
    timestamp: new Date().toISOString(),
    userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'Server-side',
  });
}

/**
 * Utility function to create a user-friendly error message
 */
export function createUserFriendlyError(error: any, defaultMessage: string = 'Something went wrong'): string {
  const extractedMessage = extractErrorMessage(error);
  
  // If we got a meaningful message from the server, use it
  if (extractedMessage && extractedMessage !== 'An unknown error occurred') {
    return extractedMessage;
  }
  
  // Otherwise, use the default message
  return defaultMessage;
}

/**
 * Wrapper for async functions to handle errors gracefully
 * @param asyncFn The async function to wrap
 * @param errorHandler Optional custom error handler
 * @returns A function that handles errors automatically
 */
export function withErrorHandling<T extends any[], R>(
  asyncFn: (...args: T) => Promise<R>,
  errorHandler?: (error: Error, context: string) => void
) {
  return async (...args: T): Promise<R | null> => {
    try {
      return await asyncFn(...args);
    } catch (error) {
      const context = asyncFn.name || 'Unknown function';
      logErrorDetails(error as Error, context);
      
      if (errorHandler) {
        errorHandler(error as Error, context);
      }
      
      return null;
    }
  };
}

/**
 * Hook to handle async operations with error handling
 * @param asyncFn The async function to execute
 * @param dependencies Dependencies for the useEffect
 * @returns Object with data, loading, error states
 */
export function useAsyncOperation<T>(
  asyncFn: () => Promise<T>,
  dependencies: any[] = []
) {
  const [data, setData] = React.useState<T | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    let mounted = true;

    const execute = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await asyncFn();
        
        if (mounted) {
          setData(result);
        }
      } catch (err) {
        if (mounted) {
          const error = err as Error;
          setError(error);
          logErrorDetails(error, 'useAsyncOperation');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    execute();

    return () => {
      mounted = false;
    };
  }, dependencies);

  return { data, loading, error };
}

/**
 * Utility to format error messages for display
 * @param error The error object
 * @returns Formatted error message
 */
export function formatErrorMessage(error: Error | unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  
  if (typeof error === 'string') {
    return error;
  }
  
  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message);
  }
  
  return 'An unexpected error occurred';
}

/**
 * Utility to check if an error is a network error
 * @param error The error object
 * @returns True if it's a network error
 */
export function isNetworkError(error: Error): boolean {
  const networkErrorMessages = [
    'fetch',
    'network',
    'connection',
    'timeout',
    'cors',
    'failed to fetch'
  ];
  
  const message = error.message.toLowerCase();
  return networkErrorMessages.some(keyword => message.includes(keyword));
}

/**
 * Utility to check if an error is an authentication error
 * @param error The error object
 * @returns True if it's an authentication error
 */
export function isAuthError(error: unknown): boolean {
  if (!error) return false;

  const err = error as { status?: number; message?: string; name?: string };

  if (err.status === 401 || err.status === 403) {
    return true;
  }

  const message = (err.message || '').toLowerCase();
  const authErrorMessages = [
    'unauthorized',
    'forbidden',
    'authentication',
    'invalid token',
    'token expired',
    'token has expired',
    'not authenticated',
    'login',
    '401',
    '403',
  ];

  return authErrorMessages.some((keyword) => message.includes(keyword));
}